import { listRequirements, updateConversationItem, getExportTemplates, exportRequirements } from '../api.js';
import { getCommonCopy, getQaChatCopy, getRequirementsListCopy, translate } from '../i18n.js';
import { showError, showSuccess } from '../utils/toast.js';
import { openDownloadOverlay } from '../utils/downloadOverlay.js';
import { registerViewCleanup } from '../utils/viewLifecycle.js';
import { createQaChatPanel } from '../components/QaChatPanel.js';
import { requirementsFilterContext } from '../components/qa/context.js';
import { eventTypeOrder, isEventsV2Enabled, labelForEventSubtype, labelForEventType } from '../utils/eventTypeLabels.js';

const html = String.raw;
const appConfig = window.__ANFORDERUNGSAI_CONFIG__ ?? {};
const EVENTS_V2_ENABLED = isEventsV2Enabled(appConfig);

const STATUS_ORDER = ['NEW', 'OPEN', 'BACKLOG', 'IN_PROGRESS', 'DONE', 'ARCHIVED'];
const PRIORITY_ORDER = ['must', 'should', 'could', "won't"];
const LEGACY_TYPE_ORDER = ['functional', 'nonfunctional', 'constraint', 'risk', 'assumption', 'open_question'];
const BACK_KEY = 'anforderungsai:conversation:back';
const EDIT_KEY = 'anforderungsai:conversation:edit';
const NONE_FILTER_VALUE = '__none__';
const DEFAULT_FILTERS = {
  search: '',
  customer: '',
  project: '',
  from: '',
  to: '',
  status: 'all',
  priority: 'all',
  type: 'all',
};

const normalizeValue = (value) => String(value ?? '').trim();
const resolveItemKey = (item) => normalizeValue(item?.sortKey ?? item?.id ?? item?.itemId ?? '');
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
const escapeAttribute = (value) => escapeHtml(value);

const formatDate = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized) return '—';
  const parsed = Date.parse(normalized.length <= 10 ? `${normalized}T00:00:00` : normalized);
  if (!Number.isFinite(parsed)) return normalized;
  return new Date(parsed).toLocaleDateString();
};

const formatPriority = (priority, copy) => {
  const normalized = String(priority ?? '').toLowerCase();
  if (!normalized) return '—';
  if (normalized.startsWith('must')) return copy.priorities.must;
  if (normalized.startsWith('should')) return copy.priorities.should;
  if (normalized.startsWith('could')) return copy.priorities.could;
  if (normalized.startsWith('won')) return copy.priorities.wont;
  return priority;
};

const formatType = (type, copy, subtype = '') => {
  const normalized = String(type ?? '');
  if (!normalized) return '—';
  const subtypeLabel = labelForEventSubtype(subtype, copy.types ?? {});
  if (EVENTS_V2_ENABLED) {
    const base = labelForEventType(normalized, copy.types ?? {});
    return subtypeLabel ? `${base} · ${subtypeLabel}` : base;
  }
  const base = copy.types[normalized] ?? normalized;
  return subtypeLabel ? `${base} · ${subtypeLabel}` : base;
};

const getTypeFilterOptions = (copy) => {
  if (!EVENTS_V2_ENABLED) {
    return LEGACY_TYPE_ORDER.map((value) => [value, copy.types?.[value] ?? value]);
  }
  return eventTypeOrder.map((value) => [value, copy.types?.[value] ?? value]);
};

const joinList = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeValue(entry)).filter(Boolean).join(', ');
  }
  return normalizeValue(value);
};

const copyToClipboard = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const buildRequirementCopyText = (req, copy) => {
  if (!req) return '';
  const lines = [];
  const addLine = (label, value) => {
    const normalized = normalizeValue(value);
    if (!normalized) return;
    lines.push(`${label}: ${normalized}`);
  };
  const status = formatStatus(req.status);
  const statusLabel = copy.status?.[status] ?? status;
  addLine(copy.table.text ?? 'Text', req.text || req.requirement);
  addLine(copy.table.customer ?? 'Kunde', req.customerId || req.customer);
  addLine(copy.table.project ?? 'Projekt', req.projectId || req.project);
  addLine(copy.table.date ?? 'Datum', formatDate(req.meetingDate || req.conversationCreatedAt));
  addLine(copy.table.type ?? 'Typ', formatType(req.eventType || req.type, copy, req.eventSubtype));
  addLine(copy.table.priority ?? 'Priorität', formatPriority(req.priority, copy));
  addLine(copy.table.status ?? 'Status', statusLabel);
  addLine(copy.table.meeting ?? 'Meeting', req.meetingDate);
  addLine(copy.table.source ?? 'Quelle', req.sourceLabel || req.source);
  addLine(copy.requirements?.speaker ?? 'Sprecher', req.sourceRange?.speaker);
  if (Number.isFinite(Number(req.sourceRange?.startSec)) && Number.isFinite(Number(req.sourceRange?.endSec))) {
    lines.push(
      `${copy.requirements?.source ?? 'Quelle'}: ${Number(req.sourceRange.startSec).toFixed(1)}s – ${Number(req.sourceRange.endSec).toFixed(1)}s`,
    );
  }
  addLine(copy.requirements?.tags ?? 'Tags', joinList(req.tags));
  addLine(copy.edit?.fields?.topics ?? 'Themenbereiche', joinList(req.categories));
  addLine('Conversation ID', req.conversationId);
  addLine('Item ID', req.sortKey || req.id);
  lines.push('');
  lines.push(JSON.stringify(req, null, 2));
  return lines.join('\n');
};

const formatStatus = (status) => String(status ?? 'NEW').toUpperCase();
const defaultSortDirection = (key) => (key === 'date' ? 'desc' : 'asc');

const downloadFile = (filename, content, mime = 'text/plain') => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const buildCsv = (rows, options = {}) => {
  const headers = Array.isArray(options.headers) && options.headers.length
    ? options.headers
    : Object.keys(rows[0] ?? {});
  const delimiter = options.delimiter ?? ',';
  const includeHeaders = options.includeHeaders !== false;
  const escapeCell = (value) => {
    const normalized = String(value ?? '');
    const needsQuotes = normalized.includes(delimiter) || normalized.includes('\n') || normalized.includes('"');
    const escaped = normalized.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };
  const lines = rows.map((row) => headers.map((header) => escapeCell(row[header])).join(delimiter));
  if (includeHeaders) {
    lines.unshift(headers.join(delimiter));
  }
  return lines.join('\n');
};

const buildMarkdown = (rows, options = {}) => {
  const headers = Array.isArray(options.headers) && options.headers.length
    ? options.headers
    : Object.keys(rows[0] ?? {});
  if (!headers.length) return '';
  const headingLevel = Math.min(Math.max(Number(options.headingLevel) || 2, 1), 6);
  const includeToc = Boolean(options.includeTableOfContents);
  const headingPrefix = `${'#'.repeat(headingLevel)} `;
  const tableHeader = `| ${headers.join(' | ')} |`;
  const tableDivider = `| ${headers.map(() => '---').join(' | ')} |`;
  const tableRows = rows.map((row) => `| ${headers.map((h) => String(row[h] ?? '')).join(' | ')} |`);
  const toc = includeToc ? `- [${options.title ?? 'Export'}](#export)\n\n` : '';
  return [
    toc,
    `${headingPrefix}Export`,
    '',
    tableHeader,
    tableDivider,
    ...tableRows,
  ].join('\n');
};

const serializeValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.map((entry) => serializeValue(entry)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    if (value.startSec !== undefined || value.endSec !== undefined) {
      const start = value.startSec ?? '';
      const end = value.endSec ?? '';
      const speaker = value.speaker ? ` (${value.speaker})` : '';
      return `${start}-${end}${speaker}`.trim();
    }
    return JSON.stringify(value);
  }
  return String(value);
};

const resolveTemplateFields = (template) => {
  const fields = Array.isArray(template?.config?.fields) ? template.config.fields : [];
  return fields
    .filter((field) => field?.enabled !== false)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const buildTemplateRows = (requirements, template) => {
  const fields = resolveTemplateFields(template);
  if (!fields.length) {
    return { headers: [], rows: [] };
  }
  const headers = fields.map((field) => field.targetName || field.sourceField);
  const rows = requirements.map((req) => {
    const row = {};
    fields.forEach((field) => {
      const key = field.targetName || field.sourceField;
      const source = field.sourceField;
      let value = '';
      if (source === 'text') value = req.text || req.requirement || '';
      else if (source === 'type') value = req.eventType ?? req.type ?? '';
      else if (source === 'eventType') value = req.eventType ?? req.type ?? '';
      else if (source === 'rationale') value = req.rationale ?? '';
      else if (source === 'priority') value = req.priority ?? '';
      else if (source === 'categories') value = serializeValue(req.categories ?? req.category ?? []);
      else if (source === 'tags') value = serializeValue(req.tags ?? []);
      else if (source === 'links') value = serializeValue(req.links ?? []);
      else if (source === 'epic') value = req.epic ?? '';
      else if (source === 'confidence') value = req.confidence ?? '';
      else if (source === 'sourceRange') value = serializeValue(req.sourceRange ?? req.source ?? '');
      else value = serializeValue(req[source] ?? '');
      row[key] = value;
    });
    return row;
  });
  return { headers, rows };
};

export const renderRequirementsListView = async (container) => {
  const copy = getRequirementsListCopy();
  const qaCopy = getQaChatCopy();
  const commonCopy = getCommonCopy();
  const state = {
    requirements: [],
    catalog: { customers: [], projects: [] },
    catalogLoaded: false,
    catalogLoading: false,
    sort: { key: 'date', direction: 'desc' },
    isLoading: true,
    isRefreshing: false,
    filters: { ...DEFAULT_FILTERS },
    saving: new Set(),
    lastError: null,
  };

  let debounceTimer = null;
  let destroyed = false;
  let closeDownloadOverlay = null;
  let templatesByFormat = null;
  let templatesLoading = false;
  const qaPanel = createQaChatPanel({
    copy: qaCopy,
    title: qaCopy.requirementsTitle,
    description: qaCopy.requirementsDescription,
    context: { scope: 'global' },
  });

  const buildFilters = () => ({
    search: normalizeValue(state.filters.search) || undefined,
    customerId: normalizeValue(state.filters.customer) || undefined,
    projectId: normalizeValue(state.filters.project) || undefined,
    from: normalizeValue(state.filters.from) || undefined,
    to: normalizeValue(state.filters.to) || undefined,
    status: state.filters.status !== 'all' ? state.filters.status : undefined,
    priority: state.filters.priority !== 'all' ? state.filters.priority : undefined,
    type: state.filters.type !== 'all' ? state.filters.type : undefined,
  });

  const buildQaContext = () => {
    const customer = normalizeValue(state.filters.customer);
    const project = normalizeValue(state.filters.project);
    return requirementsFilterContext({
      customer: customer === NONE_FILTER_VALUE ? '' : customer,
      project: project === NONE_FILTER_VALUE ? '' : project,
      from: normalizeValue(state.filters.from),
      to: normalizeValue(state.filters.to),
    });
  };

  const statusCounts = () => {
    const counts = {};
    state.requirements.forEach((req) => {
      const status = formatStatus(req.status);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  };

  const uniqueValues = (items, getter) => {
    const seen = new Set();
    items.forEach((item) => {
      const value = normalizeValue(getter(item));
      if (value) {
        seen.add(value);
      }
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }));
  };

  const deriveCatalog = (items) => ({
    customers: uniqueValues(items, (req) => req.customerId ?? req.customer ?? ''),
    projects: uniqueValues(items, (req) => req.projectId ?? req.project ?? ''),
  });

  const fetchCatalog = async () => {
    if (state.catalogLoading || state.catalogLoaded) return;
    state.catalogLoading = true;
    try {
      const payload = await listRequirements({ limit: 2000 });
      const items = Array.isArray(payload?.events) ? payload.events : (Array.isArray(payload?.requirements) ? payload.requirements : []);
      state.catalog = deriveCatalog(items);
      state.catalogLoaded = true;
    } catch (error) {
      console.warn('Failed to load requirements catalog', error);
    } finally {
      state.catalogLoading = false;
      render();
    }
  };

  const buildExportRows = () => state.requirements.map((req) => ({
    text: req.text || req.requirement || '',
    type: req.eventType ?? req.type ?? '',
    priority: req.priority ?? '',
    status: formatStatus(req.status),
    customerId: req.customerId ?? '',
    projectId: req.projectId ?? '',
    meetingDate: req.meetingDate ?? '',
    conversationId: req.conversationId ?? '',
    sortKey: req.sortKey ?? '',
  }));

  const loadTemplates = async () => {
    if (templatesByFormat || templatesLoading) return;
    templatesLoading = true;
    try {
      const data = await getExportTemplates();
      const allTemplates = [
        ...(data?.systemTemplates ?? []),
        ...(data?.userTemplates ?? []),
      ];
      templatesByFormat = {
        csv: allTemplates.filter((t) => t.format === 'csv'),
        json: allTemplates.filter((t) => t.format === 'json'),
        md: allTemplates.filter((t) => t.format === 'md'),
        xlsx: allTemplates.filter((t) => t.format === 'xlsx'),
        docx: allTemplates.filter((t) => t.format === 'docx'),
      };
    } catch (error) {
      console.warn('Failed to load export templates', error);
      templatesByFormat = { csv: [], json: [], md: [], xlsx: [], docx: [] };
    } finally {
      templatesLoading = false;
    }
  };

  const fetchRequirements = async (mode = 'load') => {
    if (mode === 'refresh') {
      state.isRefreshing = true;
    } else {
      state.isLoading = true;
    }
    state.lastError = null;
    render();
    try {
      const payload = await listRequirements(buildFilters());
      state.requirements = Array.isArray(payload?.events) ? payload.events : (Array.isArray(payload?.requirements) ? payload.requirements : []);
    } catch (error) {
      state.lastError = error;
      showError(error?.message || copy.errors.load);
    } finally {
      state.isLoading = false;
      state.isRefreshing = false;
      if (!state.catalogLoaded && !state.catalogLoading) {
        fetchCatalog();
      }
      render();
    }
  };

  const getPriorityRank = (priority) => {
    const normalized = String(priority ?? '').toLowerCase();
    const index = PRIORITY_ORDER.indexOf(normalized);
    return index === -1 ? 99 : index;
  };

  const getStatusRank = (status) => {
    const normalized = formatStatus(status);
    const index = STATUS_ORDER.indexOf(normalized);
    return index === -1 ? 99 : index;
  };

  const getSortValue = (req, key) => {
    switch (key) {
      case 'text':
        return String(req.text || req.requirement || '').toLowerCase();
      case 'customer':
        return `${normalizeValue(req.customerId ?? req.customer ?? '').toLowerCase()}\u0000${normalizeValue(
          req.projectId ?? req.project ?? '',
        ).toLowerCase()}`;
      case 'date': {
        const raw = req.meetingDate || req.conversationCreatedAt || '';
        const parsed = Date.parse(raw);
        return Number.isFinite(parsed) ? parsed : null;
      }
      case 'type':
        return String(req.eventType ?? req.type ?? '').toLowerCase();
      case 'priority':
        return getPriorityRank(req.priority);
      case 'status':
        return getStatusRank(req.status);
      case 'actions':
        return normalizeValue(req.conversationId).toLowerCase();
      default:
        return '';
    }
  };

  const compareValues = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'de', { sensitivity: 'base' });
  };

  const sortRequirements = (items) => {
    const { key, direction } = state.sort;
    if (!key) return items;
    const dir = direction === 'desc' ? -1 : 1;
    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const result = compareValues(getSortValue(a.item, key), getSortValue(b.item, key));
        return result !== 0 ? result * dir : a.index - b.index;
      })
      .map((entry) => entry.item);
  };

  const getSortIndicator = (key) => {
    if (state.sort.key !== key) return '↕';
    return state.sort.direction === 'desc' ? '↓' : '↑';
  };

const renderSortableHeader = (label, key, extraClass = '') => {
    const active = state.sort.key === key;
    const ariaSort = active ? (state.sort.direction === 'desc' ? 'descending' : 'ascending') : 'none';
    return html`
      <th class="${extraClass}" aria-sort="${ariaSort}">
        <button type="button" class="table-sort ${active ? 'active' : ''}" data-sort-key="${key}">
          <span>${label}</span>
          <span class="sort-indicator">${getSortIndicator(key)}</span>
        </button>
      </th>
    `;
  };

  const updateFilter = (field, value, immediate = false) => {
    state.filters[field] = value;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (immediate) {
      fetchRequirements('refresh');
      return;
    }
    debounceTimer = setTimeout(() => {
      fetchRequirements('refresh');
    }, 350);
  };

  const getDefaultPillValue = (field) => {
    if (field === 'customer' || field === 'project') return '';
    if (field === 'status' || field === 'priority' || field === 'type') return 'all';
    return '';
  };

  const resetFilters = () => {
    state.filters = { ...DEFAULT_FILTERS };
    fetchRequirements('refresh');
  };

  const findRequirement = (conversationId, itemKey) =>
    state.requirements.find(
      (item) =>
        normalizeValue(item.conversationId) === conversationId && resolveItemKey(item) === itemKey,
    );

  const updateStatus = async (req, conversationId, sortKey, nextStatus, selectEl, itemId) => {
    const resolvedKey = normalizeValue(sortKey) || normalizeValue(itemId);
    if (!conversationId || !resolvedKey) {
      showError(copy.errors.save);
      if (selectEl && req) {
        selectEl.value = formatStatus(req.status);
      }
      return;
    }
    const key = `${conversationId}:${resolvedKey}`;
    if (state.saving.has(key)) return;
    const currentReq = req ?? findRequirement(conversationId, resolvedKey);
    const previous = formatStatus(currentReq?.status);
    const normalized = formatStatus(nextStatus);
    state.saving.add(key);
    if (currentReq) {
      currentReq.status = normalized;
    } else {
      state.requirements = state.requirements.map((item) =>
        normalizeValue(item.conversationId) === conversationId && resolveItemKey(item) === resolvedKey
          ? { ...item, status: normalized }
          : item,
      );
    }
    if (selectEl) selectEl.disabled = true;
    try {
      const payload = { kind: 'event', updates: { status: normalized } };
      if (normalizeValue(sortKey)) {
        payload.sortKey = sortKey;
      } else if (normalizeValue(itemId)) {
        payload.itemId = itemId;
      }
      await updateConversationItem(conversationId, payload);
      showSuccess(copy.toast.statusSaved);
    } catch (error) {
      if (currentReq) {
        currentReq.status = previous;
      } else {
        state.requirements = state.requirements.map((item) =>
          normalizeValue(item.conversationId) === conversationId && resolveItemKey(item) === resolvedKey
            ? { ...item, status: previous }
            : item,
        );
      }
      if (selectEl) selectEl.value = previous;
      showError(error?.message || copy.errors.save);
    } finally {
      state.saving.delete(key);
      if (selectEl) selectEl.disabled = false;
      render();
    }
  };

  const renderSummaryCards = () => {
    const counts = statusCounts();
    const cards = STATUS_ORDER.map((status) => html`
      <div class="summary-card">
        <span class="muted tiny">${copy.status[status]}</span>
        <strong>${counts[status] || 0}</strong>
      </div>
    `);
    return html`
      <div class="summary-card">
        <span class="muted tiny">${copy.summary.total}</span>
        <strong>${state.requirements.length}</strong>
      </div>
      ${cards.join('')}
    `;
  };

  const renderTable = () => {
    if (state.isLoading) {
      return html`<div class="card"><p class="muted">${commonCopy.loading}</p></div>`;
    }
    if (!state.requirements.length) {
      return html`
        <div class="card">
          <p class="muted">${copy.empty}</p>
        </div>
      `;
    }
    return html`
      <div class="table-card">
        <table class="requirements-table">
          <thead>
            <tr>
              ${renderSortableHeader(copy.table.text, 'text')}
              ${renderSortableHeader(copy.table.customer, 'customer')}
              ${renderSortableHeader(copy.table.date, 'date')}
              ${renderSortableHeader(copy.table.type, 'type')}
              ${renderSortableHeader(copy.table.priority, 'priority')}
              ${renderSortableHeader(copy.table.status, 'status', 'status-col')}
              ${renderSortableHeader(copy.table.actions, 'actions')}
            </tr>
          </thead>
          <tbody>
            ${sortRequirements(state.requirements).map((req) => {
              const status = formatStatus(req.status);
              const conversationId = normalizeValue(req.conversationId);
              const sortKey = normalizeValue(req.sortKey);
              const itemId = normalizeValue(req.id);
              const itemKey = resolveItemKey(req);
              const isSaving = itemKey ? state.saving.has(`${conversationId}:${itemKey}`) : false;
              const encodedConversationId = conversationId ? encodeURIComponent(conversationId) : '';
              const openHref = conversationId ? `#/conversation/${encodedConversationId}` : '#/jobs';
              const metaKey = sortKey || itemId || itemKey || '';
              return html`
                <tr>
                  <td>
                    <div class="stack tight">
                      ${conversationId
                        ? html`
                            <a
                              class="jobs-detail-link"
                              data-open-conversation
                              data-conversation="${escapeAttribute(conversationId)}"
                              href="${openHref}"
                            >
                              <strong>${escapeHtml(req.text || req.requirement || '—')}</strong>
                            </a>
                          `
                        : html`<strong>${escapeHtml(req.text || req.requirement || '—')}</strong>`}
                      <div class="row muted tiny">
                        <span>${escapeHtml(conversationId.slice(0, 8))}</span>
                        <span>${escapeHtml(metaKey || '—')}</span>
                        ${req.sourceRange?.speaker ? `<span>${escapeHtml(req.sourceRange.speaker)}</span>` : ''}
                      </div>
                    </div>
                  </td>
                  <td>
                    <strong>${escapeHtml(req.customerId || '—')}</strong>
                    ${req.projectId ? `<div class="muted tiny">${escapeHtml(req.projectId)}</div>` : ''}
                  </td>
                  <td>${escapeHtml(formatDate(req.meetingDate || req.conversationCreatedAt))}</td>
                  <td>${escapeHtml(formatType(req.eventType || req.type, copy, req.eventSubtype))}</td>
                  <td>${escapeHtml(formatPriority(req.priority, copy))}</td>
                  <td class="status-col">
                    <div class="row tight requirements-status-cell">
                      <select
                        data-status-select
                        data-conversation="${escapeAttribute(conversationId)}"
                        data-sort-key="${escapeAttribute(sortKey)}"
                        data-item-id="${escapeAttribute(itemId)}"
                        ${isSaving ? 'disabled' : ''}
                      >
                        ${STATUS_ORDER.map((option) => html`
                          <option value="${option}" ${option === status ? 'selected' : ''}>${escapeHtml(copy.status[option])}</option>
                        `).join('')}
                      </select>
                      ${status === 'NEW'
                        ? html`
                            <button
                              type="button"
                              class="ghost-button button-small"
                              data-mark-open
                              data-conversation="${escapeAttribute(conversationId)}"
                              data-sort-key="${escapeAttribute(sortKey)}"
                              data-item-id="${escapeAttribute(itemId)}"
                            >
                              OK
                            </button>
                          `
                        : ''}
                    </div>
                  </td>
                  <td>
                    <div class="row tight requirements-actions">
                      ${conversationId
                        ? html`
                            <button
                              type="button"
                              class="ghost-button button-small icon-button"
                              data-copy-requirement
                              data-conversation="${escapeAttribute(conversationId)}"
                              data-item-key="${escapeAttribute(itemKey)}"
                              aria-label="${escapeAttribute(copy.table.copyLabel || 'Kopieren')}"
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <path
                                  fill="currentColor"
                                  d="M16 1H6c-1.1 0-2 .9-2 2v12h2V3h10V1zm3 4H10c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h9c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H10V7h9v14z"
                                />
                              </svg>
                            </button>
                            <button
                              type="button"
                              class="ghost-button button-small icon-button"
                              data-edit-conversation
                              data-conversation="${escapeAttribute(conversationId)}"
                              aria-label="${escapeAttribute(copy.table.editLabel || 'Bearbeiten')}"
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <path
                                  fill="currentColor"
                                  d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l8.06-8.06.92.92L5.92 19.58zM20.71 7.04a1 1 0 0 0 0-1.41L18.37 3.3a1 1 0 0 0-1.41 0l-1.62 1.62 3.75 3.75 1.62-1.62z"
                                />
                              </svg>
                            </button>
                          `
                        : ''}
                    </div>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
  };

  const render = () => {
    if (destroyed) return;
    const customers = state.catalogLoaded
      ? state.catalog.customers
      : uniqueValues(state.requirements, (req) => req.customerId ?? req.customer ?? '');
    const projects = state.catalogLoaded
      ? state.catalog.projects
      : uniqueValues(state.requirements, (req) => req.projectId ?? req.project ?? '');
    if (
      state.filters.customer &&
      state.filters.customer !== NONE_FILTER_VALUE &&
      !customers.includes(state.filters.customer)
    ) {
      customers.unshift(state.filters.customer);
    }
    if (
      state.filters.project &&
      state.filters.project !== NONE_FILTER_VALUE &&
      !projects.includes(state.filters.project)
    ) {
      projects.unshift(state.filters.project);
    }
    container.innerHTML = html`
      <div class="stack requirements-list-view">
        <div class="view-header row" style="justify-content: space-between; align-items: flex-start; gap: 1rem;">
          <div class="stack tight">
            <h1>${copy.title}</h1>
            <p class="muted">${copy.subtitle}</p>
          </div>
          <div class="row">
            <button type="button" class="ghost-button" id="requirements-refresh" ${state.isRefreshing ? 'disabled' : ''}>
              ${state.isRefreshing ? translate('common.loading') : copy.actions.refresh}
            </button>
            <button type="button" class="button button-small" id="requirements-download">
              ${copy.actions.download ?? 'Download'}
            </button>
          </div>
        </div>

        <div class="requirements-summary-grid">
          ${renderSummaryCards()}
        </div>

        <section class="card requirements-filters">
          <div class="requirements-filters-grid">
            <label class="filter-field">
              <span class="muted tiny">${copy.filters.search}</span>
              <input
                type="search"
                value="${state.filters.search}"
                placeholder="${copy.filters.searchPlaceholder}"
                data-filter="search"
              />
            </label>
            <label class="filter-field">
              <span class="muted tiny">${copy.filters.from}</span>
              <input type="date" value="${state.filters.from}" data-filter="from" />
            </label>
            <label class="filter-field">
              <span class="muted tiny">${copy.filters.to}</span>
              <input type="date" value="${state.filters.to}" data-filter="to" />
            </label>
            <button type="button" class="ghost-button" id="requirements-reset">${copy.filters.reset}</button>
          </div>
          <div class="requirements-pill-groups">
            <div class="requirements-pill-group">
              <div class="filter-pills compact">
                <button type="button" data-pill-filter="customer" data-pill-value="" class="${state.filters.customer ? '' : 'active'}">
                  ${copy.filters.customerAll}
                </button>
                <button
                  type="button"
                  data-pill-filter="customer"
                  data-pill-value="${NONE_FILTER_VALUE}"
                  class="${state.filters.customer === NONE_FILTER_VALUE ? 'active' : ''}"
                >
                  ${copy.filters.none}
                </button>
                ${customers.map((customer) => html`
                  <button
                    type="button"
                    data-pill-filter="customer"
                    data-pill-value="${escapeAttribute(customer)}"
                    class="${state.filters.customer === customer ? 'active' : ''}"
                  >
                    ${escapeHtml(customer)}
                  </button>
                `).join('')}
              </div>
            </div>
            <div class="requirements-pill-group">
              <div class="filter-pills compact">
                <button type="button" data-pill-filter="project" data-pill-value="" class="${state.filters.project ? '' : 'active'}">
                  ${copy.filters.projectAll}
                </button>
                <button
                  type="button"
                  data-pill-filter="project"
                  data-pill-value="${NONE_FILTER_VALUE}"
                  class="${state.filters.project === NONE_FILTER_VALUE ? 'active' : ''}"
                >
                  ${copy.filters.none}
                </button>
                ${projects.map((project) => html`
                  <button
                    type="button"
                    data-pill-filter="project"
                    data-pill-value="${escapeAttribute(project)}"
                    class="${state.filters.project === project ? 'active' : ''}"
                  >
                    ${escapeHtml(project)}
                  </button>
                `).join('')}
              </div>
            </div>
            <div class="requirements-pill-group">
              <div class="filter-pills">
                <button type="button" data-pill-filter="status" data-pill-value="all" class="${state.filters.status === 'all' ? 'active' : ''}">
                  ${copy.filters.statusAll}
                </button>
                <button
                  type="button"
                  data-pill-filter="status"
                  data-pill-value="${NONE_FILTER_VALUE}"
                  class="${state.filters.status === NONE_FILTER_VALUE ? 'active' : ''}"
                >
                  ${copy.filters.none}
                </button>
                ${STATUS_ORDER.map((option) => html`
                  <button
                    type="button"
                    data-pill-filter="status"
                    data-pill-value="${option}"
                    class="${state.filters.status === option ? 'active' : ''}"
                  >
                    ${copy.status[option]}
                  </button>
                `).join('')}
              </div>
            </div>
            <div class="requirements-pill-group">
              <div class="filter-pills">
                <button type="button" data-pill-filter="priority" data-pill-value="all" class="${state.filters.priority === 'all' ? 'active' : ''}">
                  ${copy.filters.priorityAll}
                </button>
                <button
                  type="button"
                  data-pill-filter="priority"
                  data-pill-value="${NONE_FILTER_VALUE}"
                  class="${state.filters.priority === NONE_FILTER_VALUE ? 'active' : ''}"
                >
                  ${copy.filters.none}
                </button>
                ${[
                  { value: 'Must', label: copy.priorities.must },
                  { value: 'Should', label: copy.priorities.should },
                  { value: 'Could', label: copy.priorities.could },
                  { value: "Won't", label: copy.priorities.wont },
                ].map((option) => html`
                  <button
                    type="button"
                    data-pill-filter="priority"
                    data-pill-value="${option.value}"
                    class="${state.filters.priority === option.value ? 'active' : ''}"
                  >
                    ${option.label}
                  </button>
                `).join('')}
              </div>
            </div>
            <div class="requirements-pill-group">
              <div class="filter-pills">
                <button type="button" data-pill-filter="type" data-pill-value="all" class="${state.filters.type === 'all' ? 'active' : ''}">
                  ${copy.filters.typeAll}
                </button>
                <button
                  type="button"
                  data-pill-filter="type"
                  data-pill-value="${NONE_FILTER_VALUE}"
                  class="${state.filters.type === NONE_FILTER_VALUE ? 'active' : ''}"
                >
                  ${copy.filters.none}
                </button>
                ${getTypeFilterOptions(copy).map(([value, label]) => html`
                  <button
                    type="button"
                    data-pill-filter="type"
                    data-pill-value="${value}"
                    class="${state.filters.type === value ? 'active' : ''}"
                  >
                    ${label}
                  </button>
                `).join('')}
              </div>
            </div>
          </div>
        </section>

        <section id="requirements-qa-chat" class="qa-chat-section"></section>

        ${renderTable()}
      </div>
    `;

    qaPanel.setHost(container.querySelector('#requirements-qa-chat'));
    qaPanel.setContext(buildQaContext());

    container.querySelectorAll('[data-filter]').forEach((input) => {
      input.addEventListener('input', (event) => {
        const field = event.target.dataset.filter;
        updateFilter(field, event.target.value);
      });
      input.addEventListener('change', (event) => {
        const field = event.target.dataset.filter;
        updateFilter(field, event.target.value);
      });
    });

    container.querySelectorAll('[data-pill-filter]').forEach((button) => {
      button.addEventListener('click', (event) => {
        const field = event.currentTarget.dataset.pillFilter;
        const value = event.currentTarget.dataset.pillValue ?? '';
        const defaultValue = getDefaultPillValue(field);
        const nextValue = value === state.filters[field] ? defaultValue : value;
        updateFilter(field, nextValue, true);
      });
    });

    const refreshButton = container.querySelector('#requirements-refresh');
    refreshButton?.addEventListener('click', () => fetchRequirements('refresh'));

    const resetButton = container.querySelector('#requirements-reset');
    resetButton?.addEventListener('click', resetFilters);

    const downloadButton = container.querySelector('#requirements-download');
    downloadButton?.addEventListener('click', async () => {
      await loadTemplates();
      const rows = buildExportRows();
      const dateSuffix = new Date().toISOString().slice(0, 10);
      if (closeDownloadOverlay) closeDownloadOverlay();
      closeDownloadOverlay = openDownloadOverlay({
        title: copy.actions.download ?? 'Download',
        subtitle: `${copy.summary?.total ?? 'Gesamt'}: ${rows.length}`,
        commonCopy,
        formats: [
          {
            id: 'csv',
            label: 'CSV',
            description: copy.actions.exportCsv ?? 'CSV Export',
          },
          {
            id: 'md',
            label: 'Markdown',
            description: copy.actions.exportMd ?? 'Markdown Export',
          },
          {
            id: 'json',
            label: 'JSON',
            description: copy.actions.exportJson ?? 'JSON Export',
            templateFormat: 'json',
          },
          {
            id: 'xlsx',
            label: 'Excel',
            description: copy.actions.exportXlsx ?? 'Vorlage erforderlich',
            requiresTemplate: true,
            templateFormat: 'xlsx',
          },
          {
            id: 'docx',
            label: 'Word',
            description: copy.actions.exportDocx ?? 'Vorlage erforderlich',
            requiresTemplate: true,
            templateFormat: 'docx',
          },
        ],
        templatesByFormat: templatesByFormat ?? {},
        manageTemplatesHref: '#/export-templates',
        onConfirm: async ({ format, template }) => {
          if (!rows.length) {
            throw new Error('Keine Daten zum Export.');
          }
          const isTemplateFormat = template && ['csv', 'json', 'md'].includes(template.format);
          const templateRows = isTemplateFormat
            ? buildTemplateRows(state.requirements, template)
            : { headers: [], rows: [] };
          const effectiveRows = templateRows.rows.length ? templateRows.rows : rows;
          const headers = templateRows.headers.length ? templateRows.headers : undefined;

          if (format === 'csv') {
            const options = template?.config?.formatOptions ?? {};
            const filename = `anforderungen_${dateSuffix}.csv`;
            downloadFile(
              filename,
              buildCsv(effectiveRows, {
                headers,
                delimiter: options.delimiter ?? ',',
                includeHeaders: options.includeHeaders !== false,
              }),
              'text/csv',
            );
            return;
          }
          if (format === 'md') {
            const options = template?.config?.formatOptions ?? {};
            const filename = `anforderungen_${dateSuffix}.md`;
            downloadFile(
              filename,
              buildMarkdown(effectiveRows, {
                headers,
                includeTableOfContents: options.includeTableOfContents ?? true,
                headingLevel: options.headingLevel ?? 2,
                title: copy.title,
              }),
              'text/markdown',
            );
            return;
          }
          if (format === 'json') {
            const options = template?.config?.formatOptions ?? {};
            const prettyPrint = options.prettyPrint !== false;
            const rootKey = options.rootKey || '';
            const arrayWrapper = options.arrayWrapper !== false;
            let payload = arrayWrapper ? effectiveRows : effectiveRows[0] ?? {};
            if (rootKey) {
              payload = { [rootKey]: payload };
            }
            const filename = `anforderungen_${dateSuffix}.json`;
            downloadFile(filename, JSON.stringify(payload, null, prettyPrint ? 2 : 0), 'application/json');
            return;
          }
          if (format === 'xlsx' || format === 'docx') {
            if (!template?.templateId) {
              throw new Error('Bitte eine Vorlage auswählen.');
            }
            const response = await exportRequirements({
              templateId: template.templateId,
              requirements: state.requirements,
            });
            const url = response?.url;
            if (!url) {
              throw new Error('Download konnte nicht erstellt werden.');
            }
            const link = document.createElement('a');
            link.href = url;
            link.download = '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
          }
          throw new Error('Unbekanntes Format.');
        },
      });
    });

    container.querySelectorAll('[data-status-select]').forEach((select) => {
      select.addEventListener('change', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const target = event.currentTarget;
        const conversationId = normalizeValue(target?.dataset.conversation);
        const sortKey = normalizeValue(target?.dataset.sortKey);
        const itemId = normalizeValue(target?.dataset.itemId);
        const itemKey = sortKey || itemId;
        const nextStatus = target?.value;
        const req = itemKey ? findRequirement(conversationId, itemKey) : null;
        if (nextStatus) {
          updateStatus(req, conversationId, sortKey, nextStatus, target, itemId);
        }
      });
    });

    container.querySelectorAll('[data-mark-open]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const target = event.currentTarget;
        const conversationId = normalizeValue(target?.dataset.conversation);
        const sortKey = normalizeValue(target?.dataset.sortKey);
        const itemId = normalizeValue(target?.dataset.itemId);
        const itemKey = sortKey || itemId;
        const req = itemKey ? findRequirement(conversationId, itemKey) : null;
        updateStatus(req, conversationId, sortKey, 'OPEN', null, itemId);
      });
    });

    container.querySelectorAll('[data-open-conversation]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = event.currentTarget;
        const conversationId = target?.dataset.conversation;
        if (!conversationId) return;
        sessionStorage.setItem(BACK_KEY, 'requirements');
      });
    });

    container.querySelectorAll('[data-edit-conversation]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const target = event.currentTarget;
        const conversationId = target?.dataset.conversation;
        if (!conversationId) return;
        sessionStorage.setItem(BACK_KEY, 'requirements');
        sessionStorage.setItem(EDIT_KEY, conversationId);
        window.location.hash = `#/conversation/${encodeURIComponent(conversationId)}`;
      });
    });

    container.querySelectorAll('[data-copy-requirement]').forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const target = event.currentTarget;
        const conversationId = normalizeValue(target?.dataset.conversation);
        const itemKey = normalizeValue(target?.dataset.itemKey);
        const req = itemKey ? findRequirement(conversationId, itemKey) : null;
        if (!req) {
          showError(copy.table.copyError || 'Kopieren fehlgeschlagen.');
          return;
        }
        try {
          const text = buildRequirementCopyText(req, copy);
          await copyToClipboard(text);
          showSuccess(copy.table.copySuccess || 'Inhalt kopiert.');
        } catch (error) {
          console.error('Copy failed', error);
          showError(copy.table.copyError || 'Kopieren fehlgeschlagen.');
        }
      });
    });

    container.querySelectorAll('[data-sort-key]').forEach((button) => {
      button.addEventListener('click', (event) => {
        const key = event.currentTarget.dataset.sortKey;
        if (!key) return;
        if (state.sort.key === key) {
          state.sort.direction = state.sort.direction === 'asc' ? 'desc' : 'asc';
        } else {
          state.sort.key = key;
          state.sort.direction = defaultSortDirection(key);
        }
        render();
      });
    });
  };

  registerViewCleanup(() => {
    destroyed = true;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (closeDownloadOverlay) {
      closeDownloadOverlay();
      closeDownloadOverlay = null;
    }
    qaPanel.destroy();
  });

  render();
  await fetchRequirements('load');
};
